<template>
  <div class="rsf-page">
    <!-- Header -->
    <div class="rsf-header">
      <p class="rsf-header__subtitle">
        Period: <strong>{{ periodLabel }}</strong> · Partner: <strong>{{ partnerName }}</strong>
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="rsf-state">
      <v-progress-circular indeterminate color="primary" size="40" width="3" />
      <p class="rsf-state__text">Loading split details…</p>
    </div>

    <template v-else-if="splitData && groupedData.length">
      <!-- Consolidated Revenue Split Data -->
      <div class="rsf-programs">
        <v-card
          v-for="(programData, i) in groupedData"
          :key="programData.program_id || i"
          outlined
          class="rsf-program"
        >
          <!-- Program Header -->
          <div class="rsf-program__header" @click="toggleProgram(i)">
            <div class="rsf-program__info">
              <v-icon small color="grey darken-1" class="mr-2">mdi-school-outline</v-icon>
              <span class="rsf-program__name">{{ programName(programData.program_name) }}</span>
            </div>
            <div class="rsf-program__total">
              <span class="rsf-program__total-label">Amount</span>
              <span class="rsf-program__total-value">{{ currency(programData.total) }}</span>
              <v-icon
                small
                class="rsf-program__chevron"
                :class="{ 'rsf-program__chevron--open': isExpanded(i) }"
              >
                mdi-chevron-down
              </v-icon>
            </div>
          </div>

          <!-- Program Content -->
          <v-expand-transition>
            <div v-show="isExpanded(i)">
              <v-divider />
              <div class="rsf-table-wrap">
                <table class="rsf-table">
                  <thead>
                    <tr>
                      <th class="rsf-th">Fee Type</th>
                      <th class="rsf-th rsf-th--num">Share %</th>
                      <th class="rsf-th rsf-th--num">Base Amount</th>
                      <th class="rsf-th rsf-th--num">GST %</th>
                      <th class="rsf-th rsf-th--num">GST Amount</th>
                      <th class="rsf-th">Type</th>
                      <th class="rsf-th rsf-th--num">Total</th>
                      <th class="rsf-th rsf-th--num">Partner Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(split, idx) in programData.splits" :key="idx">
                      <td class="rsf-td">{{ split.fee_type }}</td>
                      <td class="rsf-td rsf-td--num">{{ percent(split.share_percentage) }}</td>
                      <td class="rsf-td rsf-td--num">{{ currency(split.base_amount) }}</td>
                      <td class="rsf-td rsf-td--num">{{ percent(split.gst_percentage) }}</td>
                      <td class="rsf-td rsf-td--num">{{ currency(split.gst_amount) }}</td>
                      <td class="rsf-td">{{ split.split_type }}</td>
                      <td class="rsf-td rsf-td--num rsf-td--strong">{{ currency(split.total_amount) }}</td>
                      <td class="rsf-td rsf-td--num rsf-td--strong rsf-td--success">
                        {{ currency(split.partner_share) }}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td class="rsf-tf rsf-tf--label" colspan="2">Total</td>
                      <td class="rsf-tf rsf-tf--num"></td>
                      <td class="rsf-tf rsf-tf--num"></td>
                      <td class="rsf-tf rsf-tf--num"></td>
                      <td class="rsf-tf rsf-tf--num"></td>
                      <td class="rsf-tf rsf-tf--num rsf-tf--strong">{{ currency(programData.total) }}</td>
                      <td class="rsf-tf rsf-tf--num rsf-tf--strong rsf-tf--success">{{ currency(programData.partner_share_total) }}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </v-expand-transition>
        </v-card>
      </div>

      <!-- Summary and Finalize Section, pinned to the bottom -->
      <div class="rsf-footbar">
        <div class="rsf-summary">
          <div class="rsf-summary__info">
            <div class="rsf-summary__item">
              <span class="rsf-summary__label">Total Revenue</span>
              <span class="rsf-summary__value">{{ currency(totalRevenue) }}</span>
            </div>
            <div class="rsf-summary__item">
              <span class="rsf-summary__label">Partner Share</span>
              <span class="rsf-summary__value rsf-summary__value--success">{{ currency(totalPartnerShare) }}</span>
            </div>
            <div class="rsf-summary__item">
              <span class="rsf-summary__label">Period</span>
              <span class="rsf-summary__value">{{ periodLabel }}</span>
            </div>
          </div>

          <v-btn
            :color="alreadyFinalized ? 'primary' : 'success'"
            @click="openFinalizeDialog"
            :disabled="!splitData.length"
          >
            <v-icon small left>
              {{ alreadyFinalized ? "mdi-eye-outline" : "mdi-check-circle" }}
            </v-icon>
            {{ alreadyFinalized ? "View Transaction" : "Proceed" }}
          </v-btn>
        </div>
      </div>

      <RevenueSplitTransactionDialog
        v-model="showFinalizeDialog"
        :context="dialogContext"
        :readonly="alreadyFinalized"
        @saved="onFinalized"
      />
    </template>

    <!-- Error state -->
    <v-card v-else-if="!loading" outlined class="rsf-state rsf-state--card">
      <v-icon size="44" color="error">mdi-alert-outline</v-icon>
      <h3 class="rsf-state__title">No split data found</h3>
      <p class="rsf-state__text">{{ errorMessage }}</p>
      <v-btn color="primary" @click="goBack">Back to Summary</v-btn>
    </v-card>
  </div>
</template>

<script>
import RevenueSplitTransactionDialog from "@/components/revenueSplit/TransactionDialog.vue";
import { currency, num, percent } from "@/components/revenueSplit/transaction";

export default {
  name: 'RevenueSplitFinalize',

  components: { RevenueSplitTransactionDialog },

  data() {
    return {
      loading: false,
      submitting: false,
      splitData: null,
      errorMessage: null,
      expandedPrograms: [],
      showFinalizeDialog: false,
      // Set when this period was already finalized — the dialog then only shows
      // the recorded transaction instead of asking for one again.
      existingRecord: null,
    };
  },

  computed: {
    partner_id() {
      return this.$route.query.partner_id;
    },
    month() {
      return parseInt(this.$route.query.month);
    },
    year() {
      return parseInt(this.$route.query.year);
    },
    fortnight() {
      return this.$route.query.fortnight ? parseInt(this.$route.query.fortnight) : null;
    },

    partnerName() {
      return this.$route.query.partner_name || 'Partner';
    },

    periodLabel() {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthName = monthNames[this.month - 1] || '';
      const fortnight = this.fortnight ? ` - FN${this.fortnight}` : '';
      return `${String(this.month).padStart(2, '0')}/${this.year}${fortnight}`;
    },

    groupedData() {
      if (!this.splitData) return [];
      const grouped = {};

      this.splitData.forEach(split => {
        const programId = split.program_id;
        if (!grouped[programId]) {
          grouped[programId] = {
            program_id: programId,
            program_name: split.program_name,
            splits: [],
            total: 0,
            partner_share_total: 0,
          };
        }
        grouped[programId].splits.push(split);
        grouped[programId].total += this.num(split.total_amount);
        grouped[programId].partner_share_total += this.num(split.partner_share);
      });

      return Object.values(grouped);
    },

    totalRevenue() {
      if (!this.splitData) return 0;
      return this.splitData.reduce((sum, s) => sum + this.num(s.total_amount), 0);
    },

    totalPartnerShare() {
      if (!this.splitData) return 0;
      return this.splitData.reduce((sum, s) => sum + this.num(s.partner_share), 0);
    },

    alreadyFinalized() {
      return !!this.existingRecord;
    },

    dialogContext() {
      const record = this.existingRecord;
      return {
        historyId: record?.id || null,
        partnerId: this.partner_id,
        partnerName: this.partnerName,
        month: this.month,
        year: this.year,
        fortnight: this.fortnight,
        periodLabel: this.periodLabel,
        totalRevenue: this.totalRevenue,
        totalPartnerShare: this.totalPartnerShare,
        transaction: record
          ? {
              transaction_id: record.transaction_id || "",
              transaction_date: this.toInputDate(record.transaction_date),
              utr_number: record.utr_number || "",
              bank_name: record.bank_name || "",
              bank_account_number: record.bank_account_number || "",
              payment_mode: record.payment_mode || "",
              remarks: record.remarks || "",
            }
          : null,
      };
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Partner Revenue Split", to: { name: "Partner Revenue Split" } },
      {
        text: "Split Summary",
        to: { name: "RevenueSplitSummary", params: { id: this.partner_id } },
      },
      { text: "Finalize Revenue Split" },
    ]);
    this.loadSplitDetails();
  },

  methods: {
    async loadSplitDetails() {
      this.loading = true;
      this.errorMessage = null;

      try {
        // Get partner code from route
        const partnerCode = this.$route.query.partner_code || '';

        const params = new URLSearchParams({
          partner_id: this.partner_id,
          partner_code: partnerCode,
          month: this.month,
          year: this.year,
        });

        if (this.fortnight) {
          params.append('fortnight', this.fortnight);
        }

        const { data } = await this.$axios.get(`/revenue_split_histories?${params}`);

        // Transform data from revenue_split_histories format to split format
        const splitData = [];

        if (data.data && Array.isArray(data.data)) {
          data.data.forEach(programData => {
            if (programData.splits && Array.isArray(programData.splits)) {
              programData.splits.forEach(split => {
                splitData.push({
                  program_id: split.program_id,
                  program_name: programData.program_name,
                  fee_type: split.fee_code,
                  share_percentage: parseFloat(split.fee_percentage) || 0,
                  base_amount: parseFloat(split.amount) || 0,
                  gst_percentage: parseFloat(split.gst_percentage) || 0,
                  gst_amount: parseFloat(split.gst_amount) || 0,
                  split_type: split.gst_type,
                  total_amount: parseFloat(split.total_amount) || 0,
                  partner_share: (parseFloat(split.total_amount) * parseFloat(split.fee_percentage) / 100) || 0,
                });
              });
            }
          });
        }

        this.splitData = splitData;

        if (!this.splitData.length) {
          this.errorMessage = 'No revenue split data found for this period.';
        } else {
          this.expandedPrograms = [0];
          await this.loadExistingTransaction();
        }
      } catch (err) {
        this.errorMessage = err?.response?.data?.message || 'Unable to load split details';
        console.error('Error loading split details:', err);
      } finally {
        this.loading = false;
      }
    },

    // A period can already carry a payout; in that case the dialog is view-only.
    async loadExistingTransaction() {
      this.existingRecord = null;
      try {
        const params = new URLSearchParams({ partner_id: this.partner_id });
        const { data } = await this.$axios.get(
          `/revenue_split_histories_finalized?${params}`
        );
        const match = (data.data || []).find(
          (record) =>
            this.num(record.month) === this.month &&
            this.num(record.year) === this.year &&
            this.num(record.fortnight) === this.num(this.fortnight) &&
            record.status === "settled"
        );
        this.existingRecord = match || null;
      } catch (err) {
        // Not being able to check simply leaves the finalize flow as it was.
        console.error("Error loading existing transaction:", err);
      }
    },

    toInputDate(value) {
      if (!value) return "";
      return String(value).slice(0, 10);
    },

    programName(name) {
      if (!name) return '';
      return String(name).split('-')[0].trim();
    },

    isExpanded(index) {
      return this.expandedPrograms.includes(index);
    },

    toggleProgram(index) {
      const i = this.expandedPrograms.indexOf(index);
      if (i > -1) {
        this.expandedPrograms.splice(i, 1);
      } else {
        this.expandedPrograms.push(index);
      }
    },

    openFinalizeDialog() {
      this.showFinalizeDialog = true;
    },

    onFinalized() {
      setTimeout(() => {
        this.$router.push({
          name: 'RevenueSplitSummary',
          params: { id: this.partner_id },
        });
      }, 1000);
    },

    goBack() {
      this.$router.push({
        name: 'RevenueSplitSummary',
        params: { id: this.partner_id },
      });
    },

    num,
    currency,
    percent,
  },
};
</script>

<style scoped>
.rsf-page {
  --rsf-primary: var(--v-primary-base, #00aff1);
  --rsf-line: #e4e7ec;
  --rsf-ink: #101828;
  --rsf-ink-2: #475467;
  --rsf-ink-3: #98a2b3;
  padding: 16px 20px 104px;
  min-height: 100%;
}

/* Header */
.rsf-header {
  margin-bottom: 24px;
}

.rsf-header__back {
  margin-bottom: 12px;
}

.rsf-header__title {
  margin: 0 0 4px;
  font-size: 24px;
  font-weight: 700;
  color: var(--rsf-ink);
}

.rsf-header__subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--rsf-ink-2);
}

/* Programs */
.rsf-programs {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.rsf-program {
  border-radius: 10px;
  border-color: var(--rsf-line) !important;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  overflow: hidden;
}

.rsf-program__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;
  transition: background 0.15s;
}

.rsf-program__header:hover {
  background: #fafbfc;
}

.rsf-program__info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.rsf-program__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--rsf-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rsf-program__total {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.rsf-program__total-label {
  font-size: 11px;
  color: var(--rsf-ink-2);
  font-weight: 600;
  text-transform: uppercase;
}

.rsf-program__total-value {
  font-size: 14px;
  font-weight: 700;
  color: var(--rsf-ink);
  min-width: 100px;
  text-align: right;
}

.rsf-program__chevron {
  transition: transform 0.2s;
}

.rsf-program__chevron--open {
  transform: rotate(-180deg);
}

/* Table */
.rsf-table-wrap {
  overflow-x: auto;
}

.rsf-table {
  width: 100%;
  border-collapse: collapse;
}

.rsf-th {
  padding: 10px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--rsf-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--rsf-line);
  white-space: nowrap;
}

.rsf-th--num {
  text-align: right;
}

.rsf-td {
  padding: 10px;
  font-size: 12px;
  color: var(--rsf-ink-2);
  border-bottom: 1px solid #f4f5f7;
}

.rsf-td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rsf-td--strong {
  font-weight: 700;
  color: var(--rsf-ink);
}

.rsf-td--success {
  color: #27ae60;
}

.rsf-table tbody tr:hover {
  background: #fcfcfd;
}

.rsf-tf {
  padding: 10px;
  font-size: 12px;
  font-weight: 700;
  color: var(--rsf-ink);
  background: #f9fafb;
  border-top: 1px solid var(--rsf-line);
}

.rsf-tf--label {
  text-align: left;
}

.rsf-tf--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rsf-tf--strong {
  font-weight: 700;
}

.rsf-tf--success {
  color: #27ae60;
}

/* Bottom bar — summary and finalize, always fixed to the viewport bottom. */
.rsf-footbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 4;
  background: #fff;
  border-top: 1px solid var(--rsf-line);
  box-shadow: 0 -4px 16px -8px rgba(16, 24, 40, 0.22);
}

/* Permanent navigation drawer starts at the md breakpoint. */
@media (min-width: 960px) {
  .rsf-footbar {
    left: 268px;
  }
}

.rsf-summary {
  padding: 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}

.rsf-summary__info {
  display: flex;
  gap: 32px;
  flex: 1;
}

.rsf-summary__item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rsf-summary__label {
  font-size: 11px;
  color: var(--rsf-ink-2);
  font-weight: 600;
  text-transform: uppercase;
}

.rsf-summary__value {
  font-size: 18px;
  font-weight: 700;
  color: var(--rsf-ink);
}

.rsf-summary__value--success {
  color: #27ae60;
}

/* Dialog */
.rsf-dialog__info {
  padding: 12px;
  background: #f5f5f5;
  border-radius: 6px;
  margin-bottom: 12px;
}

.rsf-dialog__info-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 12px;
}

.rsf-dialog__label {
  color: var(--rsf-ink-2);
  font-weight: 600;
}

.rsf-dialog__value {
  color: var(--rsf-ink);
  font-weight: 600;
  text-align: right;
}

.rsf-dialog__value--success {
  color: #27ae60;
}

/* Confirmation Summary */
.rsf-confirmation-summary {
  padding: 12px;
  background: #f5f9ff;
  border: 1px solid #e3f2fd;
  border-radius: 6px;
}

.rsf-confirmation-summary__title {
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 700;
  color: var(--rsf-ink);
  display: flex;
  align-items: center;
}

.rsf-confirmation-summary__content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 12px;
}

.rsf-confirmation-summary__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rsf-confirmation-summary__section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--rsf-ink);
}

.rsf-confirmation-summary__items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rsf-confirmation-summary__item {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
}

.rsf-confirmation-summary__label {
  color: var(--rsf-ink-2);
}

.rsf-confirmation-summary__value {
  color: var(--rsf-ink);
  font-weight: 600;
  text-align: right;
}

.rsf-confirmation-summary__notice {
  padding-top: 12px;
  border-top: 1px solid #e3f2fd;
  margin-top: 12px;
}

.rsf-confirmation-summary__notice ul {
  margin: 0;
  padding-left: 20px;
  list-style: disc;
  font-size: 12px;
  color: var(--rsf-ink-2);
  line-height: 1.6;
}

/* States */
.rsf-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 56px 20px;
}

.rsf-state--card {
  margin-top: 16px;
  background: #fff;
  border: 1px solid var(--rsf-line);
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.rsf-state__text {
  margin: 12px 0 0;
  font-size: 13px;
  color: var(--rsf-ink-2);
}

.rsf-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--rsf-ink);
}




.rsf-wizard__content {
  padding: 24px !important;
  min-height: 400px;
  max-height: 60vh;
  overflow-y: auto;
}

.rsf-wizard__actions {
  padding: 16px 20px !important;
  border-top: 1px solid var(--rsf-line);
  background: #f9fafb;
}

.rsf-step {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.rsf-step__title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--rsf-ink);
}

.rsf-step__subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--rsf-ink-2);
}

.rsf-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Review Section */
.rsf-review-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #f5f9ff;
  border: 1px solid #e3f2fd;
  border-radius: 8px;
  margin-bottom: 12px;
}

.rsf-review-section__title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: var(--rsf-ink);
  display: flex;
  align-items: center;
  gap: 6px;
}

.rsf-review-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.rsf-review-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rsf-review-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--rsf-ink-2);
}

.rsf-review-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--rsf-ink);
}

.rsf-review-value--success {
  color: #27ae60;
}

.rsf-review-remarks {
  padding-top: 12px;
  border-top: 1px solid #e3f2fd;
  margin-top: 12px;
}

.rsf-review-remarks__text {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--rsf-ink);
  line-height: 1.5;
}

/* Summary Cards */
.rsf-summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.rsf-summary-card {
  border-radius: 8px;
  border-color: var(--rsf-line) !important;
  overflow: hidden;
}

.rsf-summary-card__header {
  padding: 12px 16px;
  background: #f9fafb;
  border-bottom: 1px solid var(--rsf-line);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--rsf-ink);
}

.rsf-summary-card__body {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rsf-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: var(--rsf-ink-2);
}

.rsf-summary-value {
  font-weight: 600;
  color: var(--rsf-ink);
  text-align: right;
}

.rsf-summary-value--success {
  color: #27ae60;
}

/* Notice Card */
.rsf-notice-card {
  border-radius: 8px;
  border-color: #fff3cd !important;
  background: #fffbf0;
}

.rsf-notice {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
}

.rsf-notice__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--rsf-ink);
}

.rsf-notice__text {
  margin: 0;
  font-size: 12px;
  color: var(--rsf-ink-2);
}

.rsf-notice__list {
  margin: 0;
  padding-left: 20px;
  list-style: none;
  font-size: 12px;
  color: var(--rsf-ink-2);
  line-height: 1.8;
}

.rsf-notice__list li {
  list-style: none;
  color: #27ae60;
  font-weight: 500;
}

@media (max-width: 768px) {
  .rsf-page {
    padding: 12px 12px 150px;
  }

  .rsf-summary {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 10px 14px;
  }

  .rsf-summary__info {
    justify-content: space-between;
    gap: 16px;
    width: 100%;
  }

  .rsf-confirmation-summary__content {
    grid-template-columns: 1fr;
  }

  .rsf-review-grid {
    grid-template-columns: 1fr;
  }

  .rsf-summary-cards {
    grid-template-columns: 1fr;
  }

  .rsf-wizard__content {
    min-height: auto;
    max-height: 70vh;
  }
}
</style>
